import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';

const ConfirmationModal = ({ result, onClose, onConfirm }) => {
  const [confirmText, setConfirmText] = useState('');
  const [understood, setUnderstood] = useState(false);
  
  const expectedText = 'SIMULATE MERGE';
  const canConfirm = confirmText === expectedText && understood;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border bg-error-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={24} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-error-900">Confirm Merge Simulation</h2>
              <p className="text-sm text-error-700">This action requires careful review</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Warning Notice */}
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <h3 className="font-semibold text-warning-900 mb-2 flex items-center space-x-2">
              <Icon name="Shield" size={18} />
              <span>Simulation Mode Active</span>
            </h3>
            <p className="text-sm text-warning-800">
              This is a preview simulation only. No actual data will be changed. In a production environment, 
              this operation would merge two companies and cannot be undone.
            </p>
          </div>

          {/* Companies Being Merged */}
          <div>
            <h3 className="font-semibold text-text-primary mb-3">Companies to be Merged:</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-primary-50 border border-primary-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">A</span>
                  </div>
                  <div>
                    <p className="font-medium text-text-primary">{result?.companyA?.company_name}</p>
                    <p className="text-sm text-text-secondary">Primary (will remain active)</p>
                  </div>
                </div>
                <Icon name="CheckCircle2" size={24} color="#10B981" />
              </div>

              <div className="flex justify-center">
                <Icon name="Plus" size={24} color="#64748B" />
              </div>

              <div className="flex items-center justify-between p-3 bg-secondary-50 border border-secondary-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-secondary-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">B</span>
                  </div>
                  <div>
                    <p className="font-medium text-text-primary">{result?.companyB?.company_name}</p>
                    <p className="text-sm text-text-secondary">Secondary (will be archived)</p>
                  </div>
                </div>
                <Icon name="Archive" size={24} color="#F59E0B" />
              </div>
            </div>
          </div>

          {/* Impact Summary */}
          <div className="bg-secondary-50 rounded-lg p-4">
            <h3 className="font-semibold text-text-primary mb-3">Expected Impact:</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} color="#3B82F6" />
                <span className="text-text-secondary">
                  <span className="font-medium text-text-primary">{result?.mergedPreview?.totalUsers}</span> total users after merge
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Briefcase" size={16} color="#10B981" />
                <span className="text-text-secondary">
                  <span className="font-medium text-text-primary">{result?.mergedPreview?.totalJobs}</span> total jobs after merge
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} color="#F59E0B" />
                <span className="text-text-secondary">
                  <span className="font-medium text-text-primary">{result?.conflicts?.totalConflicts}</span> conflicts to resolve
                </span>
              </div>
            </div>
          </div>

          {/* Acknowledgment Checkbox */}
          <div className="bg-white border-2 border-border rounded-lg p-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={understood}
                onChange={(e) => setUnderstood(e?.target?.checked)}
                className="mt-1 w-5 h-5 text-primary border-border rounded focus:ring-2 focus:ring-primary"
              />
              <div className="text-sm">
                <p className="font-medium text-text-primary mb-1">I understand this is a simulation</p>
                <p className="text-text-secondary">
                  I acknowledge that this is a preview only and no actual changes will be made to the database. 
                  In a real merge scenario, proper backup and rollback procedures would be required.
                </p>
              </div>
            </label>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Type <span className="font-mono bg-secondary-100 px-2 py-1 rounded">{expectedText}</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e?.target?.value)}
              placeholder="Type confirmation text..."
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-text-primary"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-secondary-50 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 border border-border text-text-primary rounded-lg font-medium hover:bg-white transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            className={`px-6 py-2.5 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2 ${
              canConfirm
                ? 'bg-primary text-white hover:bg-primary-700' :'bg-secondary-200 text-secondary-500 cursor-not-allowed'
            }`}
          >
            <Icon name="Zap" size={18} />
            <span>Run Simulation</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;